import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, X } from "lucide-react";

const FilterDropdown = ({
  label,
  options = [],
  value = "",
  onChange,
  placeholder = "Select an option",
  search = false,
  readOnly = false,
  labelClass = "text-sm font-bold text-gray-800",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false), [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(), inputRef = useRef();
  const selected = options.find(o => o.value === value);
  const filtered = search ? options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase())) : options;

  useEffect(() => {
    const handleClick = e => !dropdownRef.current?.contains(e.target) && setIsOpen(false);
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => { isOpen && search && inputRef.current?.focus(); }, [isOpen, search]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className={labelClass}>{label}</label>
      <div className="relative" ref={dropdownRef}>
        <div
          onClick={() => !readOnly && setIsOpen(!isOpen)}
          className={`flex items-center justify-between border rounded-lg px-3 py-2 cursor-pointer transition-all 
          ${isOpen ? "ring-2 ring-blue-200 border-blue-300" : ""} 
          ${readOnly ? "bg-gray-50 cursor-not-allowed" : "border-gray-300 hover:border-gray-400"}`}
        >
          <div className="flex-1">
            {isOpen && search ? (
              <div className="flex items-center">
                <Search size={16} className="text-gray-400 mr-2" />
                <input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  className="w-full bg-transparent text-sm outline-none"
                  placeholder="Search..."
                />
              </div>
            ) : (
              <span className={`text-sm ${!selected && "text-gray-500"}`}>{selected?.label || placeholder}</span>
            )}
          </div>
          {!readOnly && (
            <div className="flex items-center">
              {selected && !isOpen && (
                <button onClick={e => { e.stopPropagation(); setSearchTerm(""); onChange?.(""); }} className="p-1 hover:bg-gray-100 rounded-full mr-1">
                  <X size={16} className="text-gray-500" />
                </button>
              )}
              {isOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
            </div>
          )}
        </div>

        {!readOnly && isOpen && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {filtered.length ? (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => { setIsOpen(false); setSearchTerm(""); onChange?.(opt.value); }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 ${
                    selected?.value === opt.value ? "bg-blue-50 text-blue-600" : ""
                  }`}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm italic text-gray-500">No options found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
